import { Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
} from 'rxjs';
import {
  checkIfSpaceViewMode,
  checkIfStandAloneMode,
  SpaceViewMode,
} from '../common/utils/actions-query-params-util';
import { getSpaceIdFromPath } from '../common/utils/url';
import { InspectModeRepository } from '../state/inspect-mode-repository.service';
import { FLAGS, FlagsService } from './flags.service';
import { SpaceNavigationService } from './space-navigation.service';
import { UserService } from './user.service';

/**
 * Owns the Spaces Switcher's notion of "which space is open" and the act of switching to another
 * one. Kept in a service (rather than in the sidebar components) so every consumer — the switcher
 * list, the sidebar collapse control, and anything added later — reads the same value without the
 * nav bar having to derive or relay it.
 */
@Injectable({ providedIn: 'root' })
export class SpacesSwitcherService {
  private readonly _currentSpaceId$ = new BehaviorSubject<string>('');
  /** Id of the space the switcher should highlight; '' when not inside a space. */
  readonly currentSpaceId$ = this._currentSpaceId$.asObservable();

  /**
   * The space the user last asked the switcher to open. Holds the highlight on the intended target
   * while a switch is in flight, so it doesn't flicker onto an intermediate space the router passes
   * through (e.g. switching back to the previous space waits for the forward navigation to commit
   * first). Cleared once the URL reaches it or the switch settles.
   */
  private pendingSpaceId = '';

  /**
   * Entry points that deliberately show one space and nothing else: the embed shell, whiteboard-only
   * mode, and iframe inspect mode. Switching spaces there would break the host context (Teams/Zoom
   * tab, inspected session), so the switcher stays off regardless of the flag.
   *
   * Standalone reads the *viewer's* institution rather than the active space's: the switcher lives
   * in the app shell and has to answer this outside any space, and a session-stable answer keeps it
   * from appearing and then vanishing once a space loads.
   */
  private get isSuppressedByViewContext(): boolean {
    return (
      checkIfStandAloneMode(
        this.userService.user.getValue()?.user?.institution?.settings?.hideSpaceBackLinks,
      ) ||
      checkIfSpaceViewMode(SpaceViewMode.WHITEBOARD_ONLY) ||
      this.inspectModeRepository.isInspectMode
    );
  }

  /** True while the switcher is available. Live read, for one-shot checks inside methods. */
  get isEnabled(): boolean {
    return (
      this.flagsService.isFlagEnabled(FLAGS.SPACES_SWITCHER) && !this.isSuppressedByViewContext
    );
  }

  /**
   * The same check as a stream — the sources are triggers, not values, so it can't drift from
   * `isEnabled`. For template bindings; shared so the several async pipes bound to it don't each
   * build their own pipeline.
   */
  readonly isEnabled$ = combineLatest([
    this.flagsService.featureFlagChanged(FLAGS.SPACES_SWITCHER),
    this.userService.user,
    this.inspectModeRepository.isInspectMode$,
  ]).pipe(
    map(() => this.isEnabled),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /**
   * Resolved on demand rather than injected: this service is consumed anywhere the switcher flag is
   * read (app shell, top bar), and injecting SpaceNavigationService would build the whole session
   * navigation graph (dialogs, recording, uploads) at app boot for users who never open a space.
   */
  private get spaceNavigationService(): SpaceNavigationService {
    return this.injector.get(SpaceNavigationService);
  }

  constructor(
    private router: Router,
    private flagsService: FlagsService,
    private userService: UserService,
    private inspectModeRepository: InspectModeRepository,
    private injector: Injector,
  ) {
    this.syncFromUrl();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncFromUrl());
  }

  get currentSpaceId(): string {
    return this._currentSpaceId$.getValue();
  }

  /**
   * Opens `id` beside the sidebar reusing the in-place switch flow. Highlights the target
   * immediately (the route/guard round-trip would otherwise delay it) and marks it pending so URL
   * updates hold the highlight here through any intermediate space the switch passes through. If
   * the switch doesn't proceed (a guard or navigation error), the highlight reverts to the
   * currently open space.
   *
   * Forced: picking a space from the switcher is already an explicit one-click switch, so it skips
   * the "Leave Space?" / upload-in-progress confirmations.
   */
  switchToSpace(id: string): void {
    const previousSpaceId = this.currentSpaceId;
    this.pendingSpaceId = id;
    this._currentSpaceId$.next(id);

    this.spaceNavigationService
      .switchToSpace(id, { force: true })
      // A rejected switch (guard or navigation error) counts as "didn't switch": without this the
      // pending marker would never clear and syncFromUrl would stop tracking the URL for good.
      .catch(() => false)
      .then((switched) => {
        // A newer switch has taken over the pending highlight; leave it to that one.
        if (this.pendingSpaceId !== id) {
          return;
        }
        this.pendingSpaceId = '';
        if (!switched) {
          this._currentSpaceId$.next(previousSpaceId);
        }
      });
  }

  /**
   * Reconciles the highlight with the URL. While a switch is pending and the URL hasn't reached the
   * target yet, the highlight stays on the intended target to avoid flicker; once the URL catches
   * up (or there's no pending switch) it reconciles to the URL and clears the pending marker.
   */
  private syncFromUrl(): void {
    // Route checks should ignore query params and hashes.
    const routePath = new URL(this.router.url, window.location.origin).pathname;
    const urlSpaceId = getSpaceIdFromPath(routePath);

    if (this.pendingSpaceId && urlSpaceId !== this.pendingSpaceId) {
      return;
    }

    this.pendingSpaceId = '';
    if (urlSpaceId !== this.currentSpaceId) {
      this._currentSpaceId$.next(urlSpaceId);
    }
  }
}
